// frontend/src/main.tsx
// Punto de entrada principal de la aplicación

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { LazyMotion, MotionConfig } from 'motion/react';
import App from './App';
import { Toaster } from './components/ui/Toaster';
import { TRANSICION_BASE } from './lib/motion';
import './index.css';

// Inicializar servicio de almacenamiento
import { storage } from './services/storage';
import { getMarcasEnVuelo, esperarMarcasEnVuelo, solicitarFlushMarcas } from './lib/marcasEnVuelo';
import { esperarSyncEnCurso } from './hooks/useOfflineSync';

// Service Worker: vite-plugin-pwa (registerType: 'autoUpdate') registra el SW
// vía el registerSW.js inyectado. Ese registro es mínimo: NO recarga la pestaña
// cuando entra un build nuevo, así que el cliente abierto seguía mostrando el
// shell viejo precacheado hasta una recarga manual (de ahí el "se hardcachea").
//
// El SW ya tiene skipWaiting()+clientsClaim(), así que el SW nuevo se activa y
// toma control solo → dispara 'controllerchange'. Acá forzamos una recarga única
// en ese momento, y además chequeamos actualizaciones cada minuto para PWAs que
// quedan abiertas (kiosko de presentismo).
//
// D6 (plan de migración §1.3): si hay marcas en vuelo (cuenta regresiva de
// CadeteCard / espera de geolocalización, aún no escritas en IndexedDB) la
// recarga las perdería. Se les pide confirmar YA (solicitarFlushMarcas) y se
// espera hasta 8 s a que queden en IndexedDB; después se espera a que termine
// cualquier sincronización en curso (tope total ~10 s) y recién ahí se recarga.
// skipWaiting() del SW no cambia: el SW nuevo ya controla la página, solo se
// posterga el reload del shell.
const TOPE_RECARGA_MS = 10000;
if ('serviceWorker' in navigator) {
  let refreshing = false;
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // No recargar en la primera instalación (cuando aún no había controller):
    // en esa visita los assets ya vinieron frescos de la red.
    if (refreshing || !hadController) return;
    refreshing = true;
    void (async () => {
      const inicio = Date.now();
      if (getMarcasEnVuelo() > 0) {
        console.log('[PWA] controllerchange con marcas en vuelo: se confirman antes de recargar');
        solicitarFlushMarcas();
        const listas = await esperarMarcasEnVuelo(8000);
        if (!listas) {
          console.warn('[PWA] Marcas en vuelo tras 8 s; se recarga igual (nueva versión del SW activa)');
        }
      }
      // Sync en curso (incluida la que dispara el flush): no cortarla a mitad
      // de un push. Se le deja al menos 2 s aunque las marcas hayan agotado el tope.
      const restante = Math.max(2000, TOPE_RECARGA_MS - (Date.now() - inicio));
      const syncLibre = await esperarSyncEnCurso(restante);
      if (!syncLibre) {
        console.warn('[PWA] Sincronización aún en curso al vencer el tope; se recarga igual');
      }
      window.location.reload();
    })();
  });
  navigator.serviceWorker.ready.then((registration) => {
    setInterval(() => registration.update(), 60 * 1000);
  });
}

// Inicializar IndexedDB
storage.init().then(() => {
  console.log('Sistema de almacenamiento inicializado');
}).catch((error) => {
  console.error('Error al inicializar almacenamiento:', error);
});

// Renderizar aplicación
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Motion: features diferidas (chunk aparte) y "reducir movimiento" del sistema respetado */}
    <LazyMotion features={() => import('./lib/motionFeatures').then((m) => m.default)} strict>
      <MotionConfig reducedMotion="user" transition={TRANSICION_BASE}>
        <App />
        <Toaster />
      </MotionConfig>
    </LazyMotion>
  </StrictMode>
);
